'use client'

import type { UseInputProps } from '@pearlconvert/ui/components/input/input.types'
import type { PasswordStrength } from '@pearlconvert/ui/providers/password-strength/types'
import type { FieldValues, Path } from 'react-hook-form'

import {
  type ChangeEvent,
  FocusEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { usePasswordStrength } from '@pearlconvert/ui/providers/password-strength'

export const useInput = <TFieldValues extends FieldValues>(
  props: UseInputProps<TFieldValues>
) => {
  const { error, id, type, trigger } = props

  const handlePhoneInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, arrows, +
      if (
        [
          'Backspace',
          'Delete',
          'Tab',
          'Escape',
          'Enter',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
        ].includes(e.key) ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        e.ctrlKey ||
        e.metaKey ||
        // Allow: +
        e.key === '+'
      ) {
        return
      }
      // Prevent if not a number
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault()
      }
    },
    []
  )

  const [hasBlurred, setHasBlurred] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>()

  const { getPasswordStrength } = usePasswordStrength()

  useEffect(() => {
    setHasError(Boolean(error))
  }, [error])

  const handleBlur = async (event: FocusEvent<HTMLInputElement>) => {
    setHasBlurred(true)
    if (event.target.value.trim() !== '') {
      if (trigger) {
        const validationResult = await trigger(id as Path<TFieldValues>)
        setHasError(!validationResult)
        setHasBlurred(false)
      }
    }
  }

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (hasBlurred || hasError) {
      if (trigger) {
        await trigger(id as Path<TFieldValues>)
      }
    }
    if (type === 'password') {
      const password = event.target.value

      if (!password) {
        setPasswordStrength(undefined)
        return
      }

      setPasswordStrength(getPasswordStrength(password))
    }
  }

  const handleFocus = () => {
    if (hasBlurred && !hasError) {
      setHasBlurred(false)
    }
  }

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [setShowPassword])

  return {
    handleBlur,
    handleFocus,
    handleChange,
    handlePhoneInputKeyDown,
    showPassword,
    togglePasswordVisibility,
    passwordStrength,
  }
}
